import { gsap, ScrollTrigger, screenSm } from './1.1_animations-defaults.js';

const NAV_BTN_ID = 'nav-btn';
const NAV_BTN_ACTIVE_CLASS = 'nav-btn--active';
const NAV_BTN_MENU_ACTIVE_CLASS = 'nav-btn--menu-active';
const NAV_BTN_NAV_MENU_ACTIVE_CLASS = 'nav-btn--nav-menu-active';

const NAV_ID = 'desktop-nav';
const NAV_MODAL_ID = 'nav-modal';
const NAV_MODAL_CLASS = 'modal-nav-modal';
const NAV_MODAL_EFFECT_CLASS = 'slide';

const NAV_ACTIVE_CLASS = 'nav--active';
const NAV_MENU_ACTIVE_CLASS = 'nav--menu-active';

const NAV_SUBMENU_CLASS = 'submenu';
const NAV_MENU_CONTAINER_CLASS = 'menu-item';
const NAV_SUBMENU_CONTAINER_CLASS = 'submenu-item';
const NAV_MENU_ITEM_CLASS = 'menu-item-wrapper';
const NAV_MENU_ITEM_BACKDROP_CLASS = 'menu-item-backdrop';
const NAV_MENU_ITEM_SELECTED_CLASS = 'current-menu-item';
const NAV_MENU_PARENT_ITEM_SELECTED_CLASS = 'current-menu-parent';

const NAV_MODAL_DEFAULT_BACKDROP_CLASS = 'default-backdrop';
const NAV_MODAL_ACTIVE_BACKDROP_CLASS = 'active-backdrop';

const BUTTON_CLASS = 'button';
const BUTTON_AFTER_BG_CLASS = '--after-bg';

const NAV_MENU_ITEM_LINK_CLASS = 'menu-item-link';
const NAV_MENU_ITEM_LINK_ACTIVE_CLASS = 'menu-item-link--active';
const NAV_MENU_ITEM_BACKDROP_ACTIVE_CLASS = 'menu-item-backdrop--active';
const NAV_SUBMENU_ITEM_LINK_CLASS = 'submenu-item-link';

const MENU_ACTIVE_CLASS = 'menu--active';

const OPEN_MODAL_EVENT = 'navmodalopenmodal';
const CLOSE_MODAL_EVENT = 'navmodalclosemodal';

class DesktopNav {
    constructor() {
        this._navBtn = document.getElementById(NAV_BTN_ID);
        this._navModalCanvas = document.getElementById(NAV_MODAL_ID);

        this._navBtn && this._navModalCanvas && this._run();
    }
    _run = () => {
        this._navMenu = document.getElementById(NAV_ID);
        this._defaultBackdrop = this._navModalCanvas.querySelector(`.${NAV_MODAL_DEFAULT_BACKDROP_CLASS}`);
        this._buttons = this._navMenu.querySelectorAll(`.${BUTTON_CLASS}`);
        this._activeBackdrop = this._navModalCanvas.querySelector(`.${NAV_MODAL_ACTIVE_BACKDROP_CLASS}`);
        this._menuItemContainers = this._navMenu.querySelectorAll(`.${NAV_MENU_CONTAINER_CLASS}`);

        this._navModal = this._addModal();
        this._menuHover();
        innerWidth >= screenSm && this._checkMenuSelected();

        this._navBtn.addEventListener('click', this._toggleNav);

        document.addEventListener(
            'keydown',
            (event) => {
                if (event.key === 'Escape') {
                    event.preventDefault();
                    this._toggleNav();
                }
            },
            { once: true },
        );
    };

    _addModal = () => {
        const modal = require(MODULES_PATH + '/modal');

        return new modal({
            modalId: NAV_MODAL_ID,
            closeOnWrapperClick: false,
            center: false,
            scrollTracker: false,
            debug: false,
            scrollAdjust: true,
            backdrop: {
                effect: false,
                color: '',
                blur: 0,
            },
        });
    };

    _getDuration = (element) => {
        return parseFloat(getComputedStyle(element).transitionDuration) * 1000;
    };

    _transitionEnd = (element, func) => {
        setTimeout(func, this._getDuration(element));
    };

    _enableNavButton = () => {
        this._navBtn.addEventListener('click', this._toggleNav);
    };

    _disableNavButton = () => {
        this._navBtn.removeEventListener('click', this._toggleNav);
    };

    _toggleNav = () => {
        this._navBtn.classList.toggle(NAV_BTN_ACTIVE_CLASS);
        if (this._navBtn.classList.contains(NAV_BTN_ACTIVE_CLASS)) {
            this._disableNavButton();
            this._navModal.show();
            requestAnimationFrame(() => {
                this._navModalCanvas.classList.add(NAV_MODAL_EFFECT_CLASS);
            });
            this._transitionEnd(this._navModalCanvas, this._enableNavButton);
        } else {
            this._disableNavButton();
            requestAnimationFrame(() => {
                this._navModalCanvas.classList.remove(NAV_MODAL_EFFECT_CLASS);
                document.body.classList.remove(NAV_MODAL_CLASS);
            });

            this._transitionEnd(this._navModalCanvas, () => {
                this._navModal.hide();
            });
            this._transitionEnd(this._navModalCanvas, this._enableNavButton);
        }
    };

    _checkMenuSelected = () => {
        this._menuItemContainers.forEach((menuItemContainer) => {
            if (menuItemContainer.classList.contains(NAV_MENU_ITEM_SELECTED_CLASS) || menuItemContainer.classList.contains(NAV_MENU_PARENT_ITEM_SELECTED_CLASS)) {
                const menuItemLink = menuItemContainer.querySelector(`.${NAV_MENU_ITEM_LINK_CLASS}`);
                const backDrop = menuItemContainer.querySelector(`.${NAV_MENU_ITEM_BACKDROP_CLASS}`);
                const bgColor = getComputedStyle(backDrop).backgroundColor;
                this._defaultBackdrop.style.backgroundColor = bgColor;
                this._activeBackdrop.style.backgroundColor = bgColor;

                this._buttons &&
                    this._buttons.forEach((button) => {
                        button.style.setProperty(BUTTON_AFTER_BG_CLASS, bgColor);
                    });

                requestAnimationFrame(() => {
                    this._navMenu.classList.add(NAV_MENU_ACTIVE_CLASS);
                    this._navBtn.classList.add(NAV_BTN_NAV_MENU_ACTIVE_CLASS);
                    menuItemLink.classList.add(NAV_MENU_ITEM_LINK_ACTIVE_CLASS);
                });
            }
        });
    };

    _menuHover = () => {
        let lastTime = 0;
        let lastMouseX = 0;
        let lastMouseY = 0;

        let currEvent;
        let currTarget;

        const pointerUpdate = (e) => {
            currEvent = e;
        };

        const getMouseSpeed = () => {
            if (currEvent) {
                const currentTime = performance.now();

                const mouseX = currEvent.clientX;
                const mouseY = currEvent.clientY;

                const timeDelta = (currentTime - lastTime) / 1000;

                const distanceX = mouseX - lastMouseX;
                const distanceY = mouseY - lastMouseY;
                const distance = Math.sqrt(distanceX * distanceX + distanceY * distanceY);

                const speed = distance / timeDelta;

                lastTime = currentTime;
                lastMouseX = mouseX;
                lastMouseY = mouseY;

                return speed;
            }
        };

        const ticker = () => {
            const speed = getMouseSpeed();

            if (speed === 0) {
                if (currEvent.target.classList.contains(NAV_MENU_ITEM_LINK_CLASS)) {
                    currTarget = currEvent.target;
                    const parent = currEvent.target.parentElement.parentElement;
                    const navMenu = currEvent.target.parentElement.parentElement.parentElement;
                    const backdrop = parent.querySelector(`.${NAV_MENU_ITEM_BACKDROP_CLASS}`);
                    const menuItemLink = parent.querySelector(`.${NAV_MENU_ITEM_LINK_CLASS}`);

                    requestAnimationFrame(() => {
                        backdrop.classList.add(NAV_MENU_ITEM_BACKDROP_ACTIVE_CLASS);
                        navMenu.classList.add(NAV_ACTIVE_CLASS);
                        document.body.classList.add(MENU_ACTIVE_CLASS);
                        this._navBtn.classList.add(NAV_BTN_MENU_ACTIVE_CLASS);
                        menuItemLink.classList.add(NAV_MENU_ITEM_LINK_ACTIVE_CLASS);
                    });

                    const changeBg = (e) => {
                        const elapsedTime = e.elapsedTime * 1000;
                        if (elapsedTime > 1) {
                            const bgColor = getComputedStyle(backdrop).backgroundColor;
                            requestAnimationFrame(() => {
                                this._defaultBackdrop.style.backgroundColor = bgColor;
                            });
                        }

                        backdrop.removeEventListener('transitionend', changeBg);
                    };
                    backdrop.addEventListener('transitionend', changeBg);
                }
            } else if (speed < 2000) {
                if (currEvent.target !== currTarget && !(currEvent.target.classList.contains(NAV_SUBMENU_ITEM_LINK_CLASS) || currEvent.target.classList.contains(NAV_SUBMENU_CONTAINER_CLASS) || currEvent.target.classList.contains(NAV_SUBMENU_CLASS) || currEvent.target.classList.contains(NAV_MENU_ITEM_CLASS))) {
                    if (currTarget) {
                        if (currEvent.target.classList.contains(NAV_MENU_ITEM_LINK_CLASS)) {
                            const parent = currTarget.parentElement.parentElement;
                            const backdrop = parent.querySelector(`.${NAV_MENU_ITEM_BACKDROP_CLASS}`);
                            const menuItemLink = parent.querySelector(`.${NAV_MENU_ITEM_LINK_CLASS}`);
                            if (backdrop) {
                                requestAnimationFrame(() => {
                                    backdrop.classList.remove(NAV_MENU_ITEM_BACKDROP_ACTIVE_CLASS);
                                    menuItemLink.classList.remove(NAV_MENU_ITEM_LINK_ACTIVE_CLASS);
                                });
                            }
                        } else {
                            requestAnimationFrame(() => {
                                this._navMenu.classList.remove(NAV_ACTIVE_CLASS);
                                document.body.classList.remove(MENU_ACTIVE_CLASS);
                                this._navBtn.classList.remove(NAV_BTN_MENU_ACTIVE_CLASS);

                                this._defaultBackdrop.style.backgroundColor = this._activeBackdrop.style.backgroundColor;
                            });
                            const backdrops = this._navMenu.querySelectorAll(`.${NAV_MENU_ITEM_BACKDROP_CLASS}`);
                            const menuItemLinks = this._navMenu.querySelectorAll(`.${NAV_MENU_ITEM_LINK_CLASS}`);

                            menuItemLinks.forEach((menuItemLink) => {
                                requestAnimationFrame(() => {
                                    menuItemLink.classList.remove(NAV_MENU_ITEM_LINK_ACTIVE_CLASS);
                                });
                            });

                            const resetNav = () => {
                                backdrops.forEach((backdrop) => {
                                    requestAnimationFrame(() => {
                                        backdrop.classList.remove(NAV_MENU_ITEM_BACKDROP_ACTIVE_CLASS);
                                    });
                                });

                                this._activeBackdrop.removeEventListener('transitionend', resetNav);
                            };
                            this._activeBackdrop.addEventListener('transitionend', resetNav);
                        }
                    }
                }
            }

            this._tickerID = requestAnimationFrame(ticker);
        };

        document.addEventListener(OPEN_MODAL_EVENT, () => {
            ticker();
            addEventListener('pointermove', pointerUpdate);
        });
        document.addEventListener(CLOSE_MODAL_EVENT, () => {
            this._tickerID && cancelAnimationFrame(this._tickerID);
            removeEventListener('pointermove', pointerUpdate);
        });
    };
}
const desktopNav = new DesktopNav();
